/* eslint-disable camelcase */
import type {
  AIValidationResultDTO,
  BillingImportDTO,
  BillingIngestionBatchDTO,
  PipelineLineItemRow,
  ReviewFlagDTO,
} from '../types/pipeline.types';

/** Ids of flags “resolved” in mock mode (in-memory for the session). */
const mockResolvedReviewFlagIds = new Set<number>();

export function mockMarkReviewFlagResolved(id: number): void {
  mockResolvedReviewFlagIds.add(id);
}

export function resetMockReviewFlagStateForTests(): void {
  mockResolvedReviewFlagIds.clear();
}

const samplePrimitive = {
  description: 'Display impressions — Campaign A',
  quantity: '1000',
  unit_price: '0.05',
  suggested_sku_code: 'SKU-DISPLAY',
  client_name: 'Acme Corp',
  client_id: 'client-uuid-1',
};

export const mockBillingImports: BillingImportDTO[] = [
  {
    id: 1,
    source: 'bigquery',
    status: 'complete',
    started_at: '2026-04-01T10:00:00Z',
    completed_at: '2026-04-01T10:05:00Z',
    created_at: '2026-04-01T09:59:00Z',
    attempt: 1,
    batch_id: 100,
    correlation_id: 'corr-mock-001',
    progress: { rows: 42 },
    error_message: '',
    raw_data: {
      start_date: '2026-03-01',
      end_date: '2026-03-31',
      handler: 'BigQueryMonthlyPull',
      normalized_records: [samplePrimitive, { ...samplePrimitive, description: 'Video completions' }],
    },
  },
  {
    id: 2,
    source: 'dsp_api_1',
    status: 'failed',
    started_at: '2026-04-02T08:00:00Z',
    completed_at: '2026-04-02T08:01:00Z',
    created_at: '2026-04-02T07:59:00Z',
    attempt: 2,
    batch_id: null,
    progress: null,
    error_message: 'Upstream timeout after 30s',
    raw_data: {
      start_date: '2026-03-01',
      end_date: '2026-03-15',
      normalized_records: [],
    },
  },
];

export const mockBatches: BillingIngestionBatchDTO[] = [
  {
    id: 100,
    correlation_id: 'corr-mock-001',
    start_date: '2026-03-01',
    end_date: '2026-03-31',
    created_at: '2026-04-01T09:58:00Z',
  },
];

export function getMockPipelineLineItems(importId: number): PipelineLineItemRow[] {
  if (importId !== 1) {
    return [];
  }
  return [
    {
      id: 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa',
      billing_import: 1,
      description: 'Display impressions — Campaign A',
      qty: 1000,
      rate: 0.05,
      amount_to_bill: 50,
      validation_status: 'ok',
      attention_needed: false,
      invoice: 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb',
      details: { pipeline_primitive: samplePrimitive, client_name: 'Acme Corp', client_id: 'client-uuid-1' },
    },
    {
      id: 'cccccccc-cccc-4ccc-8ccc-cccccccccccc',
      billing_import: 1,
      description: 'Video completions',
      qty: 500,
      rate: 0.12,
      amount_to_bill: 60,
      validation_status: 'needs_review',
      attention_needed: true,
      invoice: null,
      details: { pipeline_primitive: { ...samplePrimitive, description: 'Video completions' } },
    },
  ];
}

const baseReviewFlags: ReviewFlagDTO[] = [
  {
    id: 1,
    severity: 'high',
    reason: 'Margin mismatch vs validation file',
    resolved: false,
    resolved_at: null,
    created_by_ai: true,
    created_at: '2026-04-01T12:00:00Z',
    content_type: { id: 1, app_label: 'billing', model: 'invoice' },
    object_id: 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb',
  },
  {
    id: 2,
    severity: 'medium',
    reason: 'SKU mapping uncertain',
    resolved: false,
    resolved_at: null,
    created_by_ai: true,
    created_at: '2026-04-01T12:30:00Z',
    content_type: { id: 2, app_label: 'billing', model: 'invoicelineitem' },
    object_id: 'cccccccc-cccc-4ccc-8ccc-cccccccccccc',
  },
];

export function getMockReviewFlags(resolved?: boolean): ReviewFlagDTO[] {
  const open = baseReviewFlags.filter((f) => !mockResolvedReviewFlagIds.has(f.id));
  if (resolved === true) {
    return baseReviewFlags
      .filter((f) => mockResolvedReviewFlagIds.has(f.id))
      .map((f) => ({
        ...f,
        resolved: true,
        resolved_at: new Date().toISOString(),
      }));
  }
  if (resolved === false) {
    return open;
  }
  return [...open, ...baseReviewFlags.filter((f) => mockResolvedReviewFlagIds.has(f.id))];
}

export function getMockReviewFlagById(id: number): ReviewFlagDTO | undefined {
  const fromBase = baseReviewFlags.find((f) => f.id === id);
  if (!fromBase) {
    return undefined;
  }
  if (mockResolvedReviewFlagIds.has(id)) {
    return {
      ...fromBase,
      resolved: true,
      resolved_at: new Date().toISOString(),
    };
  }
  return fromBase;
}

export const mockAiResults: AIValidationResultDTO[] = [
  {
    id: 1,
    validated_at: '2026-04-01T11:00:00Z',
    model_used: 'gpt-4o-mini',
    confidence_score: '0.92',
    status: 'flagged',
    needs_human_review: true,
    reasoning: 'Unit price differs from historical average.',
    flag_reason: 'price_anomaly',
    original_value: { rate: 0.12 },
    suggested_value: { rate: 0.1 },
    content_type: { id: 2, app_label: 'billing', model: 'invoicelineitem' },
    object_id: 'cccccccc-cccc-4ccc-8ccc-cccccccccccc',
  },
];
